import React from "react";
import {
  Badge,
  Box,
  Checkbox,
  Flex,
  Icon,
  IconButton,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  FiFolder,
  FiFileText,
  FiChevronRight,
  FiTrash2,
  FiMessageSquare,
} from "react-icons/fi";

/**
 * Portfolio-style folder card for an Exam AI subject (teal) or topic (blue).
 * Mirrors PortfolioCategoryFolderCard: left accent border, icon header, a stat
 * "shell" body with a gradient top accent, and a footer with delete + chevron.
 *
 * @param {{
 *   name: string,
 *   variant?: 'teal' | 'blue',
 *   isTopic?: boolean,
 *   statLabel: string,
 *   statValue: React.ReactNode,
 *   selected?: boolean,
 *   onToggleSelect?: () => void,
 *   onOpen?: () => void,
 *   onDelete?: () => void,
 * }} props
 */
export default function ExamFolderCard({
  name,
  variant = "teal",
  isTopic = false,
  statLabel,
  statValue,
  selected = false,
  onToggleSelect,
  onOpen,
  onChat,
  onDelete,
}) {
  const isBlue = variant === "blue";

  const cardBg = useColorModeValue("white", "gray.800");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.800", "gray.200");
  const iconPlateBg = useColorModeValue("white", "gray.700");
  const footerBg = useColorModeValue("gray.50", "gray.900");
  const dividerCol = useColorModeValue("gray.100", "gray.700");

  const iconBg = useColorModeValue(
    isBlue ? "blue.50" : "teal.50",
    isBlue ? "blue.900" : "teal.900"
  );
  const shellBg = useColorModeValue(
    isBlue ? "blue.50" : "teal.50",
    isBlue ? "blue.900" : "teal.900"
  );
  const shellBorder = useColorModeValue(
    isBlue ? "blue.200" : "teal.200",
    isBlue ? "blue.600" : "teal.600"
  );
  const labelCol = useColorModeValue("gray.500", "gray.400");

  const accent = isBlue ? "blue.400" : "teal.400";
  const hoverBorder = isBlue ? "blue.300" : "teal.300";
  const folderColor = isBlue ? "blue.500" : "teal.500";
  const topAccent = isBlue
    ? "linear(to-r, blue.400, purple.400)"
    : "linear(to-r, teal.400, cyan.400)";

  const stop = (e) => e.stopPropagation();

  return (
    <Box
      role="group"
      borderRadius="xl"
      borderWidth="1px"
      borderColor={borderColor}
      borderLeftWidth="4px"
      borderLeftColor={accent}
      bg={cardBg}
      cursor="pointer"
      overflow="hidden"
      boxShadow="sm"
      transition="all 0.2s ease"
      onClick={onOpen}
      _hover={{
        bg: hoverBg,
        boxShadow: "md",
        borderColor: hoverBorder,
        transform: "translateY(-2px)",
      }}
      h="100%"
      display="flex"
      flexDirection="column"
    >
      {/* Header: checkbox + folder icon + name + level badge */}
      <Flex
        align="center"
        gap={3}
        px={{ base: 3, md: 4 }}
        py={3}
        bg={iconBg}
        borderBottomWidth="1px"
        borderColor={borderColor}
      >
        {onToggleSelect ? (
          <Box flexShrink={0} onClick={stop}>
            <Checkbox
              colorScheme={isBlue ? "blue" : "teal"}
              isChecked={selected}
              onChange={onToggleSelect}
            />
          </Box>
        ) : null}
        <Flex
          align="center"
          justify="center"
          w="40px"
          h="40px"
          borderRadius="lg"
          bg={iconPlateBg}
          flexShrink={0}
          boxShadow="sm"
        >
          <Icon as={isTopic ? FiFileText : FiFolder} w={5} h={5} color={folderColor} />
        </Flex>
        <Box flex="1" minW={0} textAlign="left">
          <Flex align="center" gap={2} minW={0} wrap="wrap">
            <Text
              fontWeight="bold"
              fontSize={{ base: "sm", md: "md" }}
              noOfLines={2}
              color={textColor}
              title={name}
              lineHeight="short"
              flex="1"
              minW={0}
            >
              {name}
            </Text>
            <Badge
              colorScheme={isBlue ? "blue" : "teal"}
              fontSize="10px"
              borderRadius="md"
              flexShrink={0}
            >
              {isTopic ? "Topic" : "Subject"}
            </Badge>
          </Flex>
        </Box>
      </Flex>

      {/* Body: stat shell */}
      <Box px={{ base: 3, md: 4 }} pt={3} pb={3} flex="1">
        <Box
          w="100%"
          px={{ base: 3, md: 3.5 }}
          py={{ base: 2.5, md: 3 }}
          borderRadius="xl"
          bg={shellBg}
          borderWidth="1px"
          borderColor={shellBorder}
          boxShadow="sm"
          position="relative"
          overflow="hidden"
          _before={{
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            h: "3px",
            bgGradient: topAccent,
            opacity: 0.85,
          }}
        >
          <Text
            fontSize="10px"
            fontWeight="semibold"
            color={labelCol}
            textTransform="uppercase"
            letterSpacing="0.08em"
            mb={1}
          >
            {statLabel}
          </Text>
          <Text
            fontSize={{ base: "lg", md: "xl" }}
            fontWeight="extrabold"
            color={textColor}
            lineHeight="1.1"
          >
            {statValue}
          </Text>
        </Box>
      </Box>

      {/* Footer: delete + chevron */}
      <Flex
        align="center"
        justify="flex-end"
        px={{ base: 3, md: 4 }}
        py={2}
        borderTopWidth="1px"
        borderTopColor={dividerCol}
        bg={footerBg}
      >
        {onChat ? (
          <IconButton
            icon={<FiMessageSquare />}
            size="sm"
            variant="ghost"
            colorScheme={isBlue ? "blue" : "teal"}
            aria-label="Chat with AI"
            onClick={(e) => {
              stop(e);
              onChat();
            }}
          />
        ) : null}
        {onDelete ? (
          <IconButton
            icon={<FiTrash2 />}
            size="sm"
            variant="ghost"
            colorScheme="red"
            aria-label="Delete"
            onClick={(e) => {
              stop(e);
              onDelete();
            }}
          />
        ) : null}
        <Icon
          as={FiChevronRight}
          w={5}
          h={5}
          color="gray.400"
          mx={1}
          _groupHover={{ color: folderColor }}
          aria-hidden
        />
      </Flex>
    </Box>
  );
}
